import { useState } from 'react';
import {
  AI_CHAT_ATTACHMENT_MAX_BYTES,
  AI_CHAT_MAX_ATTACHMENTS,
  aiChatAttachmentKind,
  aiChatAttachmentType,
} from '@repo/lib';
import { usePageSellerId } from '@dashboard/hooks/usePage';
import { toast } from '@dashboard/context/ToastContext';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import logException from '@dashboard/services/logException';

export const ATTACHMENT_TYPE_ERROR = 'Attach images, PDFs, CSV or text files up to 10 MB.';
export const TOO_MANY_ATTACHMENTS_ERROR = `You can attach up to ${AI_CHAT_MAX_ATTACHMENTS} files to a message.`;
export const UPLOAD_FAILED_ERROR = 'A file could not be uploaded. Try again.';

let lastId = 0;

// A CSV often arrives with no type or as application/vnd.ms-excel; S3 and the API get the real one.
function normalized(file) {
  const type = aiChatAttachmentType(file);
  if (!type || file.size > AI_CHAT_ATTACHMENT_MAX_BYTES) return undefined;
  if (file.type === type) return file;
  return new File([file], file.name, { type, lastModified: file.lastModified });
}

// Documents go to the private files bucket: a CSV can hold customer data.
const bucketOf = kind => (kind === 'image' ? 'images' : 'files');

// Each file uploads as soon as it is attached, so sending only waits on the ones still going.
export default function useChatAttachments() {
  const sellerId = usePageSellerId();
  const { uploadFile } = useFileQueries();
  const [attachments, setAttachments] = useState([]);

  const drop = id =>
    setAttachments(current => {
      const attachment = current.find(item => item.id === id);
      if (attachment?.preview) URL.revokeObjectURL(attachment.preview);
      return current.filter(item => item.id !== id);
    });

  const upload = async ({ id, file, kind }) => {
    try {
      const { data, error } = await uploadFile({ file, bucket: bucketOf(kind), sellerId });
      if (error || !data?._id) throw error ?? new Error('The upload returned no file');
      setAttachments(current =>
        current.map(attachment =>
          attachment.id === id
            ? { ...attachment, fileId: data._id, ...(kind === 'image' && { url: data.location }) }
            : attachment,
        ),
      );
    } catch (error) {
      logException({ message: 'Failed to upload an AI chat attachment', error });
      toast({ title: UPLOAD_FAILED_ERROR, type: 'error' });
      drop(id);
    }
  };

  const add = fileList => {
    const files = [...fileList];
    const accepted = files.map(normalized).filter(Boolean);
    if (accepted.length < files.length) toast({ title: ATTACHMENT_TYPE_ERROR, type: 'error' });
    const room = Math.max(AI_CHAT_MAX_ATTACHMENTS - attachments.length, 0);
    if (accepted.length > room) toast({ title: TOO_MANY_ATTACHMENTS_ERROR, type: 'error' });

    const added = accepted.slice(0, room).map(file => {
      const kind = aiChatAttachmentKind(file);
      return {
        id: ++lastId,
        file,
        kind,
        name: file.name,
        type: file.type,
        ...(kind === 'image' && { preview: URL.createObjectURL(file) }),
      };
    });
    if (added.length === 0) return;
    setAttachments(current => [...current, ...added]);
    added.forEach(upload);
  };

  const clear = () => {
    attachments.forEach(attachment => {
      if (attachment.preview) URL.revokeObjectURL(attachment.preview);
    });
    setAttachments([]);
  };

  return {
    attachments,
    isFull: attachments.length >= AI_CHAT_MAX_ATTACHMENTS,
    isUploading: attachments.some(attachment => !attachment.fileId),
    uploaded: attachments.map(({ fileId, name, type, url }) => ({
      fileId,
      name,
      type,
      ...(url && { url }),
    })),
    add,
    remove: drop,
    clear,
  };
}
